import { Person as UserIcon, Search as SearchIcon, Save as SaveIcon } from '@mui/icons-material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { TargetUser } from '../types';
import { SubscriptionPlan } from '@/types/subscription';

interface UserManagementSectionProps {
  searchId: string;
  setSearchId: (id: string) => void;
  targetUser: TargetUser | null;
  onSearch: () => void;
  onUpdate: () => void;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  success: string | null;
  newPlanId: string;
  setNewPlanId: (id: string) => void;
  newStatus: string;
  setNewStatus: (status: string) => void;
  plans: SubscriptionPlan[];
  resolveResponsive: (values: { xs: number; md: number }) => number;
}

export function UserManagementSection({
  searchId,
  setSearchId,
  targetUser,
  onSearch,
  onUpdate,
  loading,
  actionLoading,
  error,
  success,
  newPlanId,
  setNewPlanId,
  newStatus,
  setNewStatus,
  plans,
  resolveResponsive
}: UserManagementSectionProps) {
  return (
    <Grid size={resolveResponsive({ xs: 12, md: 6 })}>
      <Card sx={{ height: '100%', borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>User Verification</Typography>
          <Stack direction="row" spacing={1} sx={{ mb: 4 }}>
            <TextField
              fullWidth
              label="Enter User ID or Email"
              variant="outlined"
              size="small"
              value={searchId}
              onChange={(e) => setSearchId(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onSearch()}
            />
            <Button
              variant="contained"
              onClick={onSearch}
              disabled={loading || !searchId.trim()}
              startIcon={loading ? <CircularProgress size={16} /> : <SearchIcon />}
            >
              Inspect
            </Button>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 3 }}>{success}</Alert>}

          {targetUser && (
            <Box>
              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <UserIcon sx={{ fontSize: 40, mr: 2, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h6">{targetUser.displayName || 'Anonymous'}</Typography>
                  <Typography variant="body2" color="text.secondary">{targetUser.email}</Typography>
                  <Typography variant="caption" color="text.secondary">UID: {targetUser.id}</Typography>
                </Box>
              </Box>

              <Stack spacing={3}>
                <TextField
                  select
                  fullWidth
                  label="Subscription Plan"
                  value={newPlanId}
                  onChange={(e) => setNewPlanId(e.target.value)}
                >
                  <MenuItem value="free">Free</MenuItem>
                  {plans.map(plan => (
                    <MenuItem key={plan.id} value={plan.id}>{plan.name}</MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                  <MenuItem value="expired">Expired</MenuItem>
                  <MenuItem value="trial">Trial</MenuItem>
                </TextField>

                <Button
                  fullWidth
                  variant="contained"
                  color="primary"
                  startIcon={actionLoading ? <CircularProgress size={16} /> : <SaveIcon />}
                  onClick={onUpdate}
                  disabled={actionLoading}
                >
                  Authorize Changes
                </Button>
              </Stack>
            </Box>
          )}
        </CardContent>
      </Card>
    </Grid>
  );
}
